import PageLoadedQueue from "../PageLoadedQueue";
import {TouchSpin} from "../Functions/TouchSpin";
import {Popover} from "bootstrap";
import {QuickOrderResponse} from "../Interfaces/QuickOrderResponse";

/**
 * Quick-Order Class
 *
 * The quick order class is for the quick-order input fields in the navbar and in the checkout
 * to add an item with the unique item id to the shopping list
 */
class QuickOrder {
    protected addToListUrl: URL|undefined;
    protected shouldUpdateCartPreview: boolean;
    protected quickOrderWrapper: HTMLElement;
    protected lookupResult: HTMLElement;
    protected quickOrderText: HTMLInputElement;
    protected quickOrderQuantity: HTMLInputElement;
    protected quickOrderForm: HTMLFormElement;
    protected quickOrderButton: HTMLButtonElement;
    protected successMessage: string;
    protected medicineHint: string;
    protected notForSaleHint: string;
    protected customerBlockedHint: string;
    protected minQuantity: string;
    protected maxQuantity: string;
    protected articleLookupUrl: string;

    protected articleLookupAbortController: AbortController|null = null;

    static QUICK_ORDER_EVENT = 'plandent.quickOrder.addedToList';

    constructor(quickOrderWrapper: HTMLElement) {
        if (!quickOrderWrapper.dataset.foundArticle && quickOrderWrapper.dataset.lookupArticle) {
            throw new Error('Quick-Order does not have the `found-article` and `lookup-article` data-attribute.');
        }

        this.quickOrderWrapper = quickOrderWrapper;
        this.quickOrderButton = quickOrderWrapper.querySelector('.quick-order__button');
        this.quickOrderForm = quickOrderWrapper.querySelector('.quick-order-input-form');
        this.quickOrderText = quickOrderWrapper.querySelector('.quick-order__sku');
        this.quickOrderQuantity = quickOrderWrapper.querySelector('.quantity-input');

        const addToListUrl = new URL(quickOrderWrapper.dataset.articleAddToListUrl, window.location.href);
        addToListUrl.searchParams.set('cache', new Date().getTime().toString());
        this.addToListUrl = addToListUrl;

        this.shouldUpdateCartPreview = quickOrderWrapper.dataset.updateCartPreview === '1' || quickOrderWrapper.dataset.updateCartPreview === 'true';

        this.lookupResult = quickOrderWrapper.querySelector('.quick-order__lookup-result');
        this.successMessage = quickOrderWrapper.dataset.successMessage;
        this.articleLookupUrl = quickOrderWrapper.dataset.articleLookupUrl;

        this.medicineHint = quickOrderWrapper.dataset.medicineHint;
        this.notForSaleHint = quickOrderWrapper.dataset.notForSaleHint;
        this.customerBlockedHint = quickOrderWrapper.dataset.customerBlockedHint;

        this.minQuantity = quickOrderWrapper.dataset.minQuantity;
        this.maxQuantity = quickOrderWrapper.dataset.maxQuantity;

        // Init event listeners
        this.quickOrderForm?.addEventListener('submit', this.triggerQuickOrder, {passive: false});
        this.quickOrderText?.addEventListener('input', this.skuInputEventListener, {passive: true});

        if (this.quickOrderText.value.length === 0) {
            this.lookupResult.classList.add('d-none');
        }
    }

    /**
     * Trigger if the quick order button was pressed
     *
     * Sends a request with the item id and the quantity to the backend.
     * Updates the shopping list afterward.
     */
    protected triggerQuickOrder = (e: Event) => {
        e.preventDefault();

        // check if input is valid
        if (!this.quickOrderForm.checkValidity()) {
            return;
        }

        const itemId = this.quickOrderText.value;
        const quantity = parseInt(this.quickOrderQuantity.value);
        const tmpHtml = this.quickOrderButton.innerHTML;

        this.lookupResult.classList.remove('quick-order__lookup-result--error');
        this.quickOrderButton.innerHTML = '<i class="ai ai-refresh"></i>';
        this.quickOrderButton.disabled = true;

        try {
            const addItemToListData = this.addItemToListRequest(itemId, quantity);
            addItemToListData.then((data: QuickOrderResponse) => {
                if (data.errorCode) {
                    switch (data.errorCode) {
                        case 1480326232:
                            this.lookupResult.innerHTML = this.medicineHint;
                            break;
                        case 1538978532:
                            this.lookupResult.innerHTML = this.notForSaleHint;
                            break;
                        case 1934978535:
                            this.lookupResult.innerHTML = this.customerBlockedHint;
                            break;
                    }

                    this.lookupResult.classList.add('quick-order__lookup-result--error')

                    return false;
                }

                this.lookupResult.innerHTML = '';

                const listId = data.listId;
                const list: HTMLElement = document.querySelector('.shopping-list[data-list-id="' + listId + '"]');

                this.resetInput();

                window.reloadSummary(list);
                window.updateCheckoutButtons(data.checkout.hasNormalItems, data.checkout.hasRequestItems);
                window.toggleShoppingCarts(list, data.checkout.hasNormalItems, data.checkout.hasRequestItems);

                const event = new Event(QuickOrder.QUICK_ORDER_EVENT);
                document.dispatchEvent(event);

                if (this.shouldUpdateCartPreview) {
                    window.updateCartPreviewCount(data);
                }

                if (list?.dataset?.updateListUrl) {
                    try {
                        this.updateListRequest(list).then((result) => {
                            this.updateList(list, result);
                        });
                    } catch (e) {
                        console.error(e);
                    }
                }

                const dropup: HTMLElement = this.quickOrderButton.closest('.dropup');
                if (dropup) {
                    this.changeDropup(data, dropup);
                }

                const popoverWrapper: HTMLElement = this.quickOrderButton.closest('[data-plandent-action="shopping-button-popover"]');
                if (popoverWrapper) {
                    this.changePopover(popoverWrapper);
                }

                this.quickOrderButton.innerHTML = tmpHtml;
            });
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Resets the input fields
     *
     * @param {boolean} setFocus set focus to the input field
     */
    protected resetInput = (setFocus: boolean = true) => {
        this.quickOrderText.value = '';
        this.quickOrderQuantity.value = '1';
        if (setFocus) this.quickOrderText.focus();
        this.quickOrderWrapper.dataset.foundArticle = 'false';
        this.quickOrderButton.disabled = true;
    }

    /**
     * Request to add an item to the shopping list
     *
     * @param {string} itemId the id of the item
     * @param {number} quantity quantity of the item
     */
    protected addItemToListRequest = async (itemId: string, quantity: number) => {
        const data = {
            articleItemId: itemId,
            quantity: quantity,
        }

        const response = await fetch(this.addToListUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data)
        });

        return response.json();
    }

    /**
     * Request to get the current shopping list
     *
     * @param {HTMLElement} list the shopping-list element
     */
    protected updateListRequest = async (list: HTMLElement) => {
        const updateListUrl = new URL(list.dataset.updateListUrl, window.location.href);
        updateListUrl.searchParams.set('cache', new Date().getTime().toString());

        const response = await fetch(updateListUrl, {
            method: 'GET'
        });

        return response.text();
    }

    /**
     * Update the shopping-list view and initialize shopping modules
     *
     * @param {HTMLElement} list the shopping-list element
     * @param {string} result the updated view as a html string
     */
    protected updateList = (list: HTMLElement, result: string) => {
        list.querySelector('.list-items').innerHTML = result;

        const shoppingList = document.querySelector('.shopping-list');
        TouchSpin.init(shoppingList.querySelectorAll('input[type="number"]'));

        const quantityInput: HTMLInputElement = shoppingList.querySelector('input.quantity-input');
        if (quantityInput?.type === 'text') {
            TouchSpin.init(shoppingList.querySelectorAll('input.quantity-input'));
        }

        list.classList.remove('d-none');

        const items = $(list).find('.list-items');

        items.find('*[data-annotations]').annotations();
        items.find('.price-async').prices();
        items.find('.shopping-button-async').shoppingButton();
        items.find('*[data-fetch-article-buy]').articleBuy();

        window.updateAnnotations();

        const reinitSelectpickerEvent = new Event('plandent.reinit.bootstrapDropdown');
        document.dispatchEvent(reinitSelectpickerEvent);
    }

    /**
     * Change the success messages in the dropup
     *
     * @param {QuickOrderResponse} data
     * @param {HTMLElement} dropup
     */
    protected changeDropup = (data: QuickOrderResponse, dropup: HTMLElement) => {
        const dropupSuccess: HTMLElement = dropup.closest('.success');

        if (dropupSuccess) {
            if (data.isAdjustedToMinQuantity) {
                if (dropup) {
                    this.lookupResult.innerHTML = this.quickOrderWrapper.dataset.minQuantity.replace('#quantity#', String(data.minQuantity));
                } else {
                    const minQuantityBtnText = dropupSuccess.dataset.minQuantity;
                    dropupSuccess.innerHTML = minQuantityBtnText.replace('#quantity#', String(data.minQuantity));
                }
            } else if (data.isAdjustedToMaxQuantity) {
                if (dropup) {
                    this.lookupResult.innerHTML = this.maxQuantity.replace('#quantity#', String(data.maxQuantity));
                } else {
                    const maxQuantityBtnText = dropupSuccess.dataset.maxQuantity;
                    dropupSuccess.innerHTML = maxQuantityBtnText.replace('#quantity#', String(data.maxQuantity));
                }
            } else {
                this.lookupResult.innerHTML = this.successMessage;
                dropupSuccess.innerHTML = dropupSuccess.dataset.default;
            }
        }
    }

    /**
     * Toggle the popover and hide it after a few seconds
     *
     * @param {HTMLElement} popoverWrapper
     */
    protected changePopover = (popoverWrapper: HTMLElement) => {
        const popoverContent = popoverWrapper.querySelector('[data-plandent-shopping-button="content"]').innerHTML;
        const popover = new Popover(this.quickOrderButton, {
            html: true,
            content: () => {
                return popoverContent;
            },
            trigger: 'manual',
            customClass: 'shopping-button-dropup-popover quick-order-header-popover',
            popperConfig: {
                // @ts-ignore
                placement: 'top-end'
            }
        });
        popover.show();

        async function autoHidePopover() {
            await setTimeout(() => {
                popover.hide();
            }, 5000);
        }

        autoHidePopover();
    }

    /**
     * Toggle the icon of the quick order button
     */
    protected toggleQuickOrderButtonStatus = (loading: boolean = true) => {
        const refresh = this.quickOrderButton.querySelector('.icon-refresh');
        const shoppingCart = this.quickOrderButton.querySelector('.icon-shopping-cart');

        if (refresh && shoppingCart) {
            if (loading) {
                refresh.classList.remove('d-none');
                shoppingCart.classList.add('d-none');
            } else {
                refresh.classList.add('d-none');
                shoppingCart.classList.remove('d-none');
            }
        }
    }

    /**
     * event-listener for the sku input
     *
     * @param e
     */
    protected skuInputEventListener = (e: Event) => {
        this.articleLookupAbortController?.abort();

        if (!this.articleLookupUrl) {
            return
        }

        this.quickOrderButton.disabled = true;

        this.lookupResult.innerHTML = '';

        if (this.quickOrderText.value.length > 0) {
            this.toggleQuickOrderButtonStatus(true);
            this.lookupResult.classList.remove('quick-order__lookup-result--error');
            this.lookupResult.classList.remove('d-none');

            try {
                this.articleLookupAbortController = new AbortController();
                this.articleLookupRequest(this.quickOrderText.value)
                    .then((data) => {
                        if (data) {
                            this.lookupResult.innerHTML = '<strong>' + data.title + '</strong>';
                            this.lookupResult.append(', ' + data.description);

                            for (let i in data.attributes) {
                                const key = data.attributes[i].key;
                                const value = data.attributes[i].value;

                                if (value === null) continue;

                                switch (key) {
                                    case 'vpe.packung':
                                        this.lookupResult.append(', Packung: ' + value);
                                        break;
                                    case 'sku.inhalt':
                                        this.lookupResult.append(', Inhalt: ' + value);
                                        break;
                                }

                                this.quickOrderButton.disabled = false;
                                this.quickOrderWrapper.dataset.foundArticle = 'true';
                            }
                        } else {
                            this.lookupResult.classList.add('quick-order__lookup-result--error');
                            this.lookupResult.innerHTML = '<strong>' + this.lookupResult.dataset.noResultText + '</strong>';
                            this.quickOrderButton.disabled = true;
                            this.quickOrderWrapper.dataset.foundArticle = 'false';
                        }
                    })
                    .catch((reason) => {
                        this.lookupResult.classList.add('quick-order__lookup-result--error');
                        this.lookupResult.innerHTML = '<strong>' + this.lookupResult.dataset.noResultText + '</strong>';
                        this.quickOrderButton.disabled = true;
                        this.quickOrderWrapper.dataset.foundArticle = 'false';
                    })
                    .finally(() => {
                        this.toggleQuickOrderButtonStatus(false);
                    });
            } catch (e) {
                console.error(e);
            }
        } else {
            this.lookupResult.classList.remove('quick-order__lookup-result--error');
            this.lookupResult.classList.add('d-none');
        }
    }

    /**
     * Request to get an article based on the article sku
     *
     * @param articleSku
     */
    protected articleLookupRequest = async (articleSku: string) => {
        const url = new URL(this.articleLookupUrl, window.location.href);

        const data = {
            articleSku: articleSku,
        };

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
            signal: this.articleLookupAbortController?.signal
        });

        this.articleLookupAbortController = null;

        return response.json();
    }
}

export default QuickOrder;

PageLoadedQueue.addFunction(() => {
    const quickOrderInputs = document.querySelectorAll('.quick-order-input');
    if (quickOrderInputs.length > 0) {
        quickOrderInputs.forEach((quickOrderInputWrapper: HTMLElement) => {
            new QuickOrder(quickOrderInputWrapper);
        });
    }

    const quickOrderDrop: HTMLElement = document.querySelector('#quick-order-drop');
    if (quickOrderDrop) {
        quickOrderDrop.addEventListener('show.bs.dropdown', () => {
            const input: HTMLInputElement = quickOrderDrop.nextElementSibling.querySelector('.quick-order__sku');
            input.focus();
        }, {passive: true});
    }
});
